import {
  ApplicationConfig,
  linkedSignal,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  BaseRouteReuseStrategy,
  provideRouter,
  Route,
  RouteReuseStrategy,
  TitleStrategy,
  withExperimentalAutoCleanupInjectors,
} from '@angular/router';
import { environment } from '../environments/environment';
import { platform } from '../platforms/platform';
import { palettenUmlagerungRoutes } from '@softapps/lager/paletten-umlagerung';
import {
  lagerplatzUmlagerungProviders,
  lagerplatzUmlagerungRoutes,
} from '@softapps/lager/lagerplatz-umlagerung';
import {
  lagerkorrekturProviders,
  lagerkorrekturRoutes,
} from '@softapps/lager/lagerkorrektur';
import { inventurProviders, inventurRoutes } from '@softapps/lager/inventur';
import {
  kommissionierungRoutes,
  provideKommissionierung,
  provideVorKommissionierung,
  vorKommissionierungRoutes,
} from '@softapps/lager/kommissionierung';
import { lagerstandProviders, lagerstandRoutes } from '@softapps/lager/lagerstand';
import {
  lagerkorrekturArtikelProviders,
  lagerkorrekturArtikelRoutes,
} from '@softapps/lager/lagerkorrektur-artikel';
import {
  lagerCoreProviders,
  SOFTLINE_CONFIG_LAGERPLATZ_AUSWAHL,
} from '@softapps/lager/core';
import { lagerInventurRoutes as mdeInventurRoutes } from '@softapps/mde/inventur';
import { artikelstammRoutes, provideWwsArtikelstamm } from '@softapps/wws/artikelstamm';
import {
  provideStorageLocation,
  storageLocationRoutes,
} from '@softapps/mde/storage-location';
import {
  productRelocationRoutes,
  providePalletRelocationModule,
} from '@softapps/lager/product-relocation';
import { mdeInventoryRoutes, provideMdeInventoryModule } from '@softapps/mde/inventory';
import {
  palettenkorrekturRoutes,
  providePalettenkorrektur,
} from '@softapps/lager/palettenkorrektur';
import {
  provideDarkTheme,
  remoteConfigProviders,
  SOFTLINE_CONFIG_DEFAULT_THEME,
  SOFTLINE_CONFIG_LANGUAGES,
  SOFTLINE_CONFIG_THEMES,
  SOFTLINE_LANGUAGE_DE,
  SOFTLINE_LANGUAGE_EN,
  SoftlineTitleStrategy,
  TitleService,
} from '@softline/application';
import { provideWwsCoreItemSearchConfig, wwsCoreProviders } from '@softapps/wws/core';
import { fibuCoreProviders, kundeninfoLeisteProviders } from '@softapps/fibu/core';
import {
  dashboardProviders,
  dashboardRoutes,
  provideKundeninfoWidget,
} from '@softapps/allgemein/dashboard';
import {
  provideWarenuebernahmeModule,
  warenuebernahmeRoutes,
} from '@softapps/wws/warenuebernahme';
import { artikelInfoProviders, artikelInfoRoutes } from '@softapps/wws/artikel-info';
import {
  BelegDownloadStrategy,
  gesamtabfrageProviders,
  gesamtabfrageRoutes,
  SOFTLINE_CONFIG_GESAMTABFRAGE,
} from '@softapps/wws/gesamtabfrage';
import { artikelvorratProviders, artikelvorratRoutes } from '@softapps/mde/artikelvorrat';
import {
  bestellvorschlagProviders,
  bestellvorschlagRoutes,
} from '@softapps/mde/bestellvorschlag';
import {
  bestellerfassungProviders,
  bestellerfassungRoutes,
} from '@softapps/mde/bestellerfassung';
import {
  etikettendruckProviders,
  etikettendruckRoutes,
} from '@softapps/mde/etikettendruck';
import { retourenProviders, retourenRoutes } from '@softapps/mde/retouren';
import {
  warenverprobungProviders,
  warenverprobungRoutes,
} from '@softapps/mde/warenverprobung';
import { statusabfrageProviders, statusabfrageRoutes } from '@softapps/pms/statusabfrage';
import {
  stempelerfassungProviders,
  stempelerfassungRoutes,
} from '@softapps/pms/stempelerfassung';
import {
  lieferscheinProviders,
  lieferscheinRoutes,
  SOFTLINE_CONFIG_LIEFERSCHEIN,
} from '@softapps/wws/lieferschein';
import { checklisteProviders, checklisteRoutes } from '@softapps/allgemein/checkliste';
import { freigabeProviders } from '@softapps/fibu/freigabe';
import { kreditlimitProviders } from '@softapps/wws/kreditlimit';
import {
  umsatzstatistikProviders,
  umsatzstatistikRoutes,
} from '@softapps/wws/umsatzstatistik';
import { haftruecklassProviders } from '@softapps/fibu/haftruecklass';
import { offenePostenProviders } from '@softapps/fibu/offene-posten';
import { kundenabfrageProviders, kundenabfrageRoutes } from '@softapps/wws/kundenabfrage';
import { mdcInventoryRouteFactory, provideMdcInventory } from '@softapps/mdc/inventory';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  provideSoftApps,
  SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING,
  SOFTLINE_ROUTES_APPS,
  SOFTLINE_ROUTES_EMBEDDED,
  SOFTLINE_ROUTES_PUBLIC,
  SOFTLINE_ROUTES_TERMINAL,
} from '@softapps/core';
import {
  moduleRoutes,
  provideModuleOverview,
  SOFTLINE_CONFIG_APP_OVERVIEW,
} from '@softapps/allgemein/modules';
import {
  accountContextProviders,
  connectionSettingsRoutes,
  contextSettingsProviders,
  settingsProviders,
  settingsRoutes,
  userContextProviders,
  utilitiesProviders,
  vkforgContextProviders,
} from '@softapps/allgemein/utils';
import { loginRoutes, withLogin } from '@softapps/allgemein/login';
import { passwordProviders, passwordRoutes } from '@softapps/allgemein/password';
import {
  provideNativeTooltipOverride,
  SOFTLINE_CONFIG_MESSAGE_BAR_DURATION,
} from '@softline/ui-core';
import { Title } from '@angular/platform-browser';
import {
  SOFTLINE_CONFIG_AUTHENTICATION_FORWARD_ROUTE,
  SOFTLINE_CONFIG_JWT_EXPIRATION_TIME,
  SOFTLINE_CONFIG_JWT_REFRESH_TIME,
} from '@softline/auth';
import { default as localeDe } from '@angular/common/locales/de';
import { provideRoster } from '@softapps/pms/roster';
import { withDeliveryNoteRoster } from '@softline/delivery-note-roster';
import { provideDeliveryNoteCorrectionModule } from '@softapps/wws/delivery-note-correction';

registerLocaleData(localeDe, 'de');

export class SoftAppsRouteReuseStrategy extends BaseRouteReuseStrategy {
  override shouldDestroyInjector(route: Route): boolean {
    return route?.data?.['destroyInjector'] === true ? true : false;
  }
}

const appName = linkedSignal(() => 'Holz Rentsch');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter([], withExperimentalAutoCleanupInjectors()),
    {
      provide: RouteReuseStrategy,
      useClass: SoftAppsRouteReuseStrategy,
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true }),

    {
      provide: SOFTLINE_ROUTES_APPS,
      useValue: [
        ...gesamtabfrageRoutes,
        ...artikelInfoRoutes,
        ...bestellvorschlagRoutes,
        ...bestellerfassungRoutes,
        ...etikettendruckRoutes,
        ...retourenRoutes,
        ...warenverprobungRoutes,

        ...artikelvorratRoutes,
        ...warenuebernahmeRoutes,

        ...stempelerfassungRoutes,
        ...statusabfrageRoutes,
        ...lieferscheinRoutes,
        ...umsatzstatistikRoutes,
        ...checklisteRoutes,

        ...dashboardRoutes,
        ...kundenabfrageRoutes,

        ...moduleRoutes,
        ...settingsRoutes,
        ...palettenUmlagerungRoutes,
        ...lagerplatzUmlagerungRoutes,
        ...lagerkorrekturRoutes,
        ...inventurRoutes,
        ...mdeInventurRoutes,
        ...kommissionierungRoutes,
        ...lagerstandRoutes,
        ...lagerkorrekturArtikelRoutes,
        ...artikelstammRoutes,
        ...storageLocationRoutes,
        ...productRelocationRoutes,

        ...mdeInventoryRoutes,
        ...palettenkorrekturRoutes,
        ...vorKommissionierungRoutes,
        ...mdcInventoryRouteFactory({}),
      ],
      multi: true,
    },
    {
      provide: SOFTLINE_ROUTES_TERMINAL,
      useValue: [],
      multi: true,
    },
    {
      provide: SOFTLINE_ROUTES_PUBLIC,
      useValue: [
        ...loginRoutes,
        ...passwordRoutes,
        ...connectionSettingsRoutes,
        ...settingsRoutes,
      ],
      multi: true,
    },
    {
      provide: SOFTLINE_ROUTES_EMBEDDED,
      useValue: [...moduleRoutes, ...statusabfrageRoutes, ...dashboardRoutes],
      multi: true,
    },

    provideSoftApps(
      { connection: environment.connection },
      withLogin()
      //withRegistration()
    ),
    {
      provide: SOFTLINE_CONFIG_APP_OVERVIEW,
      useValue: {
        isGroupButtonActive: true,
        defaultView: 'notGrouped',
        disableNoPermissionApps: true,
        isModulesSortedByAlphabet: false,
      },
    },
    { provide: SOFTLINE_CONFIG_DEFAULT_THEME, useValue: 'holz-rentsch' },
    {
      provide: SOFTLINE_CONFIG_THEMES,
      useValue: {
        key: 'holz-rentsch',
        name: 'Holz Rentsch',
      },
      multi: true,
    },
    provideDarkTheme(false),

    provideNativeTooltipOverride(),
    {
      provide: TitleStrategy,
      useFactory: (title: Title, service: TitleService) =>
        new SoftlineTitleStrategy(appName(), title, service),
      deps: [Title, TitleService],
    },
    {
      provide: SOFTLINE_CONFIG_LANGUAGES,
      useValue: [SOFTLINE_LANGUAGE_DE, SOFTLINE_LANGUAGE_EN],
    },

    ...userContextProviders,
    ...remoteConfigProviders,
    ...contextSettingsProviders,
    ...accountContextProviders,
    ...vkforgContextProviders,
    ...utilitiesProviders,
    ...settingsProviders,
    ...passwordProviders,
    provideModuleOverview({
      preserveProviderOrder: true,
    }),

    //Wws
    provideWwsCoreItemSearchConfig({ useMatchcodeAsTitle: true }),
    ...wwsCoreProviders,
    ...fibuCoreProviders,
    ...kundeninfoLeisteProviders,
    ...provideKundeninfoWidget(),
    ...dashboardProviders,

    // Lager
    ...lagerCoreProviders,
    provideVorKommissionierung({
      showReKundeInsteadOfKunde: true,
      disablePalettenbegleitscheine: true,
      showLessInfoInArticleView: true,
      showConfirmationOnFinalize: true,
    }),
    // ...kommissionierungProviders,
    provideKommissionierung({
      showReKundeInsteadOfKunde: true,
      disablePalettenbegleitscheine: true,
      showLessInfoInArticleView: true,
      showConfirmationOnFinalize: false,
    }),

    provideWarenuebernahmeModule({
      dierektannahme: true,
      reklamation: true,
      lagerplatzauswahl: true,
      ekBarcode: true,
      palletDetailFlow: 'splitModalOnly',
    }),

    ...providePalletRelocationModule(),
    providePalettenkorrektur(),
    ...lagerplatzUmlagerungProviders,

    ...lagerkorrekturProviders,
    ...lagerkorrekturArtikelProviders,
    ...inventurProviders,
    provideMdcInventory(),
    ...lagerstandProviders,
    ...provideMdeInventoryModule(),

    ...artikelInfoProviders,
    ...provideStorageLocation(),

    ...provideWwsArtikelstamm({
      readonly: false,
      hasProductDescriptionFeature: true,
    }),

    // Gesamtabfrage (Belege)
    ...gesamtabfrageProviders,
    {
      provide: SOFTLINE_CONFIG_GESAMTABFRAGE,
      useValue: {
        geschaeftsfall: true,
        download: [
          {
            belegart: 'Rechnung',
            strategies: [BelegDownloadStrategy],
          },
          {
            belegart: 'Lieferschein',
            strategies: [BelegDownloadStrategy],
          },
          {
            belegart: 'Auftrag',
            strategies: [BelegDownloadStrategy],
          },
          {
            belegart: 'Offert',
            strategies: [BelegDownloadStrategy],
          },
          {
            belegart: 'Kommissionierschein',
            strategies: [BelegDownloadStrategy],
          },
        ],
      },
    },

    // MDE Module
    ...artikelvorratProviders,
    ...bestellvorschlagProviders,
    ...bestellerfassungProviders,
    ...etikettendruckProviders,
    provideMdeInventoryModule(),
    ...retourenProviders,
    provideStorageLocation(),
    ...warenverprobungProviders,

    // PMS
    ...statusabfrageProviders,
    ...stempelerfassungProviders,

    ...lieferscheinProviders,
    { provide: SOFTLINE_CONFIG_LIEFERSCHEIN, useValue: { pallet: true } },
    ...provideDeliveryNoteCorrectionModule(),
    ...checklisteProviders,

    ...freigabeProviders,
    ...kreditlimitProviders,
    ...umsatzstatistikProviders,
    ...haftruecklassProviders,
    ...offenePostenProviders,
    ...kundenabfrageProviders,

    provideRoster(withDeliveryNoteRoster()),

    ...platform.providers,
    { provide: LOCALE_ID, useValue: 'de' },
    { provide: SOFTLINE_CONFIG_LAGERPLATZ_AUSWAHL, useValue: 'scanAndSelect' },
    {
      provide: SOFTLINE_CONFIG_AUTHENTICATION_FORWARD_ROUTE,
      useValue: '/',
    },
    {
      provide: SOFTLINE_CONFIG_MESSAGE_BAR_DURATION,
      useValue: 5000,
    },
    {
      provide: SOFTLINE_CONFIG_JWT_EXPIRATION_TIME,
      useValue: 604_800, // 7 days
    },
    {
      provide: SOFTLINE_CONFIG_JWT_REFRESH_TIME,
      useValue: 172_800, // 2 days
    },

    { provide: SOFTLINE_CONFIG_PUBLIC_SHELL_BRANDING, useValue: true },
  ],
};
